// @flow
import React, { memo, type Element, useState, useEffect } from 'react';
import { TextFieldStyled } from './ComponentTypes';
import { isNumber, removeLeadingZeros } from 'helpers';

type Props = {
  classes?: Object,
  value?: boolean | Function | any,
  onChange?: Function | false,
  autoFocus?: boolean,
  variant?: boolean,
  helperText?: any,
  error?: boolean | null,
  inputLabelProps?: Object,
  invalidErrorMessage?: string
};

const TextField = ({
  classes,
  value,
  variant,
  onChange,
  enableEnterKey = false,
  integerOnly = false,
  autoFocus,
  helperText,
  invalidErrorMessage,
  inputLabelProps = {},
  startAdornment,
  endAdornment,
  ...props
}: Props): Element<*> => {
  const [newValue, setValue] = useState(value);
  const [newError, setNewError] = useState(props.error);
  const [errorMessage, setErrorMessage] = useState('');
  const isOnchange = typeof onChange === 'function';
  const variantValue = variant ? 'standard' : 'outlined';

  useEffect(() => {
    if (!isOnchange) setValue(value);
  }, [value]);

  useEffect(() => {
    setNewError(props.error);
  }, [props.error]);

  useEffect(() => {
    if (value) {
      setNewError(false);
      setErrorMessage('');
    }
  }, [value]);

  const handleValid = e => {
    if (e.target.value) {
      setNewError(false);
      setErrorMessage('');
    }
  };

  const handleInvalid = e => {
    e.preventDefault();
    setNewError(!e.target.validity.valid);
    setErrorMessage(invalidErrorMessage || e.target.validationMessage);
  };

  const textFieldOnKeyPress = e => {
    if ((integerOnly && !isNumber(e.key)) || (!enableEnterKey && e.key === 'Enter')) {
      e.preventDefault();
    }
  };

  const handlePaste = e => {
    const txt = e.clipboardData.getData('Text');
    if (integerOnly && (!isNumber(txt) || !txt.match(/^[0-9]+$/))) {
      e.preventDefault();
    }
  };

  const handleChange = e => {
    if (props.type === 'number') {
      e.target.value = removeLeadingZeros(e.target?.value);
    }

    if (isOnchange) onChange(e);
    else setValue(e.target.value);
  };

  return (
    <TextFieldStyled
      {...props}
      value={isOnchange ? value : newValue}
      onChange={handleChange}
      onInput={e => handleValid(e)}
      onKeyPress={textFieldOnKeyPress}
      onPaste={handlePaste}
      classes={{ root: `variant-${variantValue}` }}
      margin="none"
      width={1}
      InputLabelProps={{ classes: { focused: 'field-focused' }, autoFocus, ...inputLabelProps }}
      variant={variantValue}
      onInvalid={handleInvalid}
      error={props.error || newError}
      helperText={errorMessage || helperText}
    />
  );
};

TextField.defaultProps = {
  classes: {},
  autoFocus: false,
  value: '',
  onChange: false,
  variant: false,
  helperText: '',
  error: false,
  inputLabelProps: {}
};

export default memo<Props>(TextField);
